import React from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ConferenceRoom, ViewMode } from './types';
import { focusVisibleSx, toolbarSx } from './stiklainisStyles';

interface StiklainisCalendarToolbarProps {
  dateRangeLabel: string;
  viewMode: ViewMode;
  rooms: ConferenceRoom[];
  selectedRoomId: string;
  onRoomChange?: (roomId: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onViewModeChange: (mode: ViewMode) => void;
  onCreateReservation: () => void;
}

const StiklainisCalendarToolbar: React.FC<StiklainisCalendarToolbarProps> = ({
  dateRangeLabel,
  viewMode,
  rooms,
  selectedRoomId,
  onRoomChange,
  onPrevious,
  onNext,
  onToday,
  onViewModeChange,
  onCreateReservation,
}) => {
  const { t } = useTranslation();
  const todayLabel =
    viewMode === 'week' ? t('stiklainis.thisWeek') : t('stiklainis.today');

  return (
    <Box sx={toolbarSx}>
      <Stack spacing={1.25} alignItems="center" sx={{ width: '100%' }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ fontWeight: 700, fontSize: '1.15rem', textAlign: 'center' }}
        >
          {dateRangeLabel}
        </Typography>

        <ButtonGroup size="small" variant="outlined" aria-label={t('stiklainis.calendarNav')}>
          <Button
            onClick={onPrevious}
            aria-label={t('stiklainis.previous')}
            sx={focusVisibleSx}
          >
            {t('stiklainis.previous')}
          </Button>
          <Button onClick={onToday} aria-label={todayLabel} sx={focusVisibleSx}>
            {todayLabel}
          </Button>
          <Button onClick={onNext} aria-label={t('stiklainis.next')} sx={focusVisibleSx}>
            {t('stiklainis.next')}
          </Button>
        </ButtonGroup>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            pt: 0.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          {rooms.length > 1 && onRoomChange && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="stiklainis-room-select-label">{t('stiklainis.room')}</InputLabel>
              <Select
                labelId="stiklainis-room-select-label"
                label={t('stiklainis.room')}
                value={selectedRoomId || ''}
                onChange={(e) => onRoomChange(String(e.target.value))}
                sx={focusVisibleSx}
              >
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    {room.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <ButtonGroup size="small" aria-label={t('stiklainis.viewMode')}>
            <Button
              variant={viewMode === 'week' ? 'contained' : 'outlined'}
              onClick={() => onViewModeChange('week')}
              aria-pressed={viewMode === 'week'}
              sx={focusVisibleSx}
            >
              {t('stiklainis.weekView')}
            </Button>
            <Button
              variant={viewMode === 'month' ? 'contained' : 'outlined'}
              onClick={() => onViewModeChange('month')}
              aria-pressed={viewMode === 'month'}
              sx={focusVisibleSx}
            >
              {t('stiklainis.monthView')}
            </Button>
          </ButtonGroup>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onCreateReservation}
            aria-label={t('stiklainis.newReservation')}
            sx={focusVisibleSx}
          >
            {t('stiklainis.newReservation')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default StiklainisCalendarToolbar;
